import React from "react";
import { useTranslation } from "react-i18next";
import { Leaf, Utensils, ShieldAlert, RefreshCw } from "lucide-react";
import { InfoCard } from "./InfoCard";

/**
 * UsesCard — historical medicinal + non-medicinal cultural uses.
 *
 * Two sub-sections rendered inside a single bento card:
 *   1. **Medicinal / traditional uses** — always paired with a strict
 *      "not medical advice, consult a professional" disclaimer. Empty when
 *      Gemini couldn't find well-documented herbal history (or the plant is
 *      toxic — the prompt tells the model to return an empty array in that
 *      case).
 *   2. **Cultural uses** — culinary, ceremonial, dye, fiber, wildlife
 *      forage. No medical disclaimer here since none of these items make
 *      health claims.
 *
 * `onRegenerate` (optional) surfaces a small ↻ pill in the card header so
 * the user can re-run the LLM against the current identification when the
 * existing prose reads wrong (e.g. an old Gemini call called a non-native
 * plant "native" in the cultural_uses text).
 *
 * Returns `null` when BOTH lists are empty AND there's no regenerate hook
 * so the parent doesn't need a guard — same convention as PestIssuesCard.
 */
export const UsesCard = ({ medicinal = [], cultural = [], onRegenerate, regenerating = false }) => {
  const { t } = useTranslation();
  const med = Array.isArray(medicinal) ? medicinal.filter(Boolean) : [];
  const cul = Array.isArray(cultural) ? cultural.filter(Boolean) : [];
  if (med.length === 0 && cul.length === 0 && !onRegenerate) return null;

  return (
    <InfoCard
      title={t("result.usesTitle")}
      icon={<Leaf className="w-4 h-4" strokeWidth={1.5} />}
      testId="card-uses"
      trailing={onRegenerate ? (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          data-testid="uses-regenerate"
          aria-label={t("result.usesRegenerateAria")}
          title={t("result.usesRegenerate")}
          className="h-6 px-2 rounded-full bg-white border border-[#E8E5DF] text-leaf-mist hover:text-leaf hover:border-leaf/40 text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1 transition disabled:opacity-60"
        >
          <RefreshCw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.75} />
          {regenerating ? t("result.usesRegenerating") : t("result.usesRegenerate")}
        </button>
      ) : null}
    >
      {med.length > 0 && (
        <div data-testid="uses-medicinal">
          <p className="text-[11px] uppercase tracking-[0.18em] text-leaf font-medium mb-1.5 flex items-center gap-1.5">
            <ShieldAlert className="w-3 h-3" strokeWidth={1.5} />
            {t("result.usesMedicinalLabel")}
          </p>
          <ul className="space-y-1.5 text-leaf-mist list-disc pl-4">
            {med.map((line, i) => (
              <li key={`med-${i}`} className="text-sm leading-snug">{line}</li>
            ))}
          </ul>
          <p
            className="mt-2 text-[11px] leading-snug text-rust/90 bg-[#FFF4DC] border border-[#E9C46A]/40 rounded-xl px-2.5 py-1.5"
            data-testid="uses-medical-disclaimer"
          >
            {t("result.usesMedicalDisclaimer")}
          </p>
        </div>
      )}

      {cul.length > 0 && (
        <div data-testid="uses-cultural" className={med.length > 0 ? "mt-4" : ""}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-leaf font-medium mb-1.5 flex items-center gap-1.5">
            <Utensils className="w-3 h-3" strokeWidth={1.5} />
            {t("result.usesCulturalLabel")}
          </p>
          <ul className="space-y-1.5 text-leaf-mist list-disc pl-4">
            {cul.map((line, i) => (
              <li key={`cul-${i}`} className="text-sm leading-snug">{line}</li>
            ))}
          </ul>
        </div>
      )}
    </InfoCard>
  );
};
